/**
 * Meal Track Base MUI Theme Configurations
 *
 * This file contains the base Material-UI theme configurations established by Meal Track for the application.
 *
 * Note: Client-specific configurations will override any settings defined in this file.
 * Modify these configurations as needed to customize the Material-UI theme for the Meal Track project.
 */
import { alpha, createTheme, useTheme } from "@mui/material";
import produce from "immer";
export const useMealTrackThemeConfigurations = () => {
    // We start with the default MUI theme values. The final theme will be a combination of these values and the client-specific values.
    const DEFAULT_FONTS = [
        /**
         * FONT SIZE QUICK REFERENCE
         * 100        (thin)
         * 100italic  (thin)
         * 300        (light)
         * 300italic  (light)
         * 400        (regular)
         * 400italic  (regular)
         * 500        (medium)
         * 500italic  (medium)
         * 700        (bold)
         * 700italic  (bold)
         * 900        (black)
         * 900italic  (black)
         *
         * weights: ["100", "100italic", "300", "300italic", "400", "400italic", "500", "500italic", "700", "700italic", "900", "900italic"],
         */
        {
            font: "Roboto",
            weights: [
                "100",
                "100italic",
                "300",
                "300italic",
                "400",
                "400italic",
                "500",
                "500italic",
                "700",
                "700italic",
                "900",
                "900italic",
            ],
        },
        {
            font: "Suez One",
            weights: [
                "100",
                "100italic",
                "300",
                "300italic",
                "400",
                "400italic",
                "500",
                "500italic",
                "700",
                "700italic",
                "900",
                "900italic",
            ],
        },
        {
            font: "Open Sans",
            weights: [
                "100",
                "100italic",
                "300",
                "300italic",
                "400",
                "400italic",
                "500",
                "500italic",
                "700",
                "700italic",
                "900",
                "900italic",
            ],
        },
        {
            font: "Lato",
            weights: [
                "100",
                "100italic",
                "300",
                "300italic",
                "400",
                "400italic",
                "500",
                "500italic",
                "700",
                "700italic",
                "900",
                "900italic",
            ],
        },
    ];
    const CUSTOM_BREAKPOINTS = {
        //? ===== USING BREAKPOINTS =====
        //? useMediaQuery takes a key (breakpoint) and uses the following Range Types: up, down, only, not, between.
        //? up: Matches screen widths greater than or equal to the screen size defined by the breakpoint key (inclusive).
        //* theme.breakpoints.up(key) => media query
        //? down: Matches screen widths less than the screen size defined by the breakpoint key (exclusive).
        //* theme.breakpoints.down(key) => media query
        //? only: Matches screen widths starting from the screen size defined by the breakpoint key (inclusive)
        //? and stopping at the screen size defined by the next breakpoint key (exclusive).
        //* theme.breakpoints.only(key) => media query
        //? not: Matches screen widths outside the range of the specified breakpoint,
        //? i.e., below the start of the given breakpoint or above the end of the next one.
        //* theme.breakpoints.not(key) => media query
        //? between: Matches screen widths greater than or equal to the screen size defined by the first breakpoint key (inclusive)
        //? and less than the screen size defined by the second breakpoint key (exclusive).
        //* theme.breakpoints.between(start, end) => media query
        //? When used in the sx prop, the breakpoint range is the same as that of the "up" helper.
        //? E.g., sx breakpoints match screen widths greater than or equal to the screen size defined by the breakpoint key (inclusive).
        //* <Box
        //*   sx={{
        //*     padding: {
        //*       xx: 1,    // padding: 8px for screens 0px and up (custom zero breakpoint)
        //*       xs: 2,    // padding: 16px for screens 320px and up
        //*       sm: 3,    // padding: 24px for screens 600px and up
        //*     },
        //*   }}
        //* >
        breakpoints: {
            values: {
                xx: 0,
                xs: 320,
                sm: 600,
                md: 960,
                container: 1140,
                lg: 1280,
                xl: 1920,
            },
        },
    };
    const DEFAULT_TYPOGRAPHY = {
        typography: {
            htmlFontSize: 16,
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: 14,
            fontWeightLight: 300,
            fontWeightRegular: 400,
            fontWeightMedium: 500,
            fontWeightBold: 700,
            h1: {
                fontFamily: '"Suez One", serif',
                fontWeight: 300,
                fontSize: "2rem",
                lineHeight: 1.167,
                letterSpacing: "-0.01562em",
            },
            h2: {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 400,
                fontSize: "1.625rem",
                lineHeight: 1.2,
                letterSpacing: "-0.00833em",
            },
            h3: {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 500,
                fontSize: "1.25rem",
                lineHeight: 1.2,
                letterSpacing: "-0.00833em",
            },
            h4: {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 500,
                fontSize: "1rem",
                lineHeight: 1.167,
                letterSpacing: "0em",
            },
            h5: {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 400,
                fontSize: "0.875rem",
                lineHeight: 1.334,
                letterSpacing: "0em",
            },
            h6: {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 400,
                fontSize: ".75rem",
                lineHeight: 1.235,
                letterSpacing: "0.00735em",
            },
            h7: {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 300,
                fontSize: ".75rem",
                lineHeight: 1.334,
                letterSpacing: "0em",
            },
            subtitle1: {
                //do we need the subtitles? is this the default? perhaps they should be 0.875rem too
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 400,
                fontSize: "1rem",
                lineHeight: 1.75,
                letterSpacing: "0.00938em",
            },
            subtitle2: {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 500,
                fontSize: "0.875rem",
                lineHeight: 1.57,
                letterSpacing: "0.00714em",
            },
            body1: {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 400,
                fontSize: "1rem",
                lineHeight: 1.5,
                letterSpacing: "0.00938em",
            },
            body2: {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 400,
                fontSize: ".875rem",
                lineHeight: 1.43,
                letterSpacing: "0.01071em",
            },
            body3: {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 400,
                fontSize: ".8125rem",
                lineHeight: 1.66,
                letterSpacing: "0.03333em",
            },
            button: {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 500,
                fontSize: "0.875rem",
                lineHeight: 1.75,
                letterSpacing: "0.02857em",
                textTransform: "none",
            },
            caption: {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 400,
                fontSize: "0.75rem",
                lineHeight: 1.66,
                letterSpacing: "0.03333em",
            },
            overline: {
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                fontWeight: 400,
                fontSize: "0.75rem",
                lineHeight: 2.66,
                letterSpacing: "0.08333em",
                textTransform: "none",
            },
        },
    };
    const OVERRIDES = {
        components: {
            MuiAccordionSummary: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        backgroundColor: alpha(theme.palette.primary.light, 0.3),
                    }),
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        // Not a fan of using !important, but it's necessary here to override the padding added when the appbar is sticky and a modal/popup/popper/menu is open.
                        // This is a MUI regression issue and has to do with padding being added to accommodate the scrollbar.
                        paddingRight: "0 !important",
                        "&.nav-appbar-overrides": {
                            // Default styles for navbar.
                            position: "static",
                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                            // This is setup for client configuration
                            backgroundColor: theme.palette.common.white,
                            color: theme.palette.common.black,
                        },
                        "&.footer-appbar-overrides": {
                            // Default styles for footer.
                            top: "auto",
                            bottom: 0,
                            position: "static",
                            // This is setup for client configuration
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                        },
                    }),
                },
            },
            MuiChip: {
                variants: [
                    {
                        props: {
                            variant: "filledSquared",
                        },
                        style: {
                            borderRadius: "4px",
                        },
                    },
                    {
                        props: {
                            variant: "outlinedSquared",
                        },
                        style: {
                            borderRadius: "4px",
                        },
                    },
                ],
                variantMapping: {
                    filledSquared: "filled",
                    outlinedSquared: "outlined", // Render outlinedSquared variant as a outlined variant.
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        fontSize: theme.typography.body1.fontSize,
                        color: theme.palette.common.black,
                        "&.Mui-focused": {
                            // This ensures that the label color is black when focused.
                            color: theme.palette.common.black,
                            fontWeight: 500,
                        },
                    }),
                },
            },
            MuiSelect: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        fontSize: theme.typography.body1.fontSize,
                        backgroundColor: alpha(theme.palette.primary.light, 0.1),
                        // If we are using an alpha on the primary or secondary light color, the contrast text should be black.
                        color: theme.palette.common.black,
                    }),
                },
            },
            MuiToolbar: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        "&.topbar-overrides": {
                            minHeight: 0,
                            "@media screen and (min-width: 0px)": {
                                minHeight: 0,
                            },
                        },
                    }),
                },
            },
            MuiTypography: {
                variants: [
                    {
                        props: {
                            variant: "h7",
                        },
                        style: {},
                    },
                    {
                        props: {
                            variant: "body3",
                        },
                        style: {},
                    },
                ],
                variantMapping: {
                    h7: "h6",
                    body3: "p", // Render body3 variant as p element
                },
            },
        },
    };
    const MEAL_TRACK_THEME_CONFIGURATIONS = Object.assign(Object.assign(Object.assign({}, CUSTOM_BREAKPOINTS), DEFAULT_TYPOGRAPHY), OVERRIDES);
    const MT_THEME = createTheme(Object.assign({}, MEAL_TRACK_THEME_CONFIGURATIONS));
    return {
        MT_THEME,
        DEFAULT_FONTS,
    };
};
/**
 * Converts the fontSize properties in the clientThemeConfigurations object from pixel units to rem units.
 * This function is a temporary measure to complete the ticket. When further updating the
 * theme in the future, this function should be removed and all font sizes should be
 * converted to rem units when they are set in KMS or when being mapped and sent to
 * the database. However, this would require a significant amount of work and testing
 * that would significantly expand the scope of the ticket.
 * @param clientThemeConfigurations The client-specific theme configurations to convert.
 * @returns The clientThemeConfigurations object with fontSize properties converted to rem units.
 */
export const configureClientTheme = (clientThemeConfigurations) => {
    const { palette: { augmentColor }, } = useTheme();
    // Use immer to create and update the clientThemeConfigurations object without mutating the original.
    const clientThemeObj = produce(clientThemeConfigurations, (draft) => {
        var _a, _b;
        // Recursively traverse the object and convert all fontSize properties to rem units.
        const traverse = (draft) => {
            for (const key in draft) {
                if (typeof draft[key] === "object" && draft[key] !== null) {
                    traverse(draft[key]);
                }
                else if (key === "fontSize" && typeof draft[key] === "string") {
                    // The fontSize property is a numeric string that represents a pixel unit.
                    // We append the px unit to the string and then use the mui pxToRem function to convert it to rem unit.
                    draft[key] = useTheme().typography.pxToRem(parseInt(draft[key]));
                }
                else if (key === "disableRipple" && typeof draft[key] === "string") {
                    // Convert string boolean values to actual booleans
                    draft[key] = draft[key] === "true";
                }
                else if (key === "fontWeight" && typeof draft[key] === "string") {
                    // Convert Google Fonts variant names to CSS font-weight values
                    // "regular" → "400", "italic" → "400" (with fontStyle handling elsewhere)
                    if (draft[key] === "regular" || draft[key] === "italic") {
                        draft[key] = "400";
                    }
                    // Extract numeric weight from strings like "700italic"
                    else if (draft[key].includes("italic")) {
                        draft[key] = draft[key].replace("italic", "");
                    }
                }
            }
        };
        traverse(draft);
        // If the client theme has a primary color defined, augment that color for primary light, dark, and contrastText values.
        if ((_a = draft.palette) === null || _a === void 0 ? void 0 : _a.primary) {
            draft.palette.primary = augmentColor({ color: draft.palette.primary });
        }
        // If the client theme has a secondary color defined, augment that color for the secondary light, dark, and contrastText values.
        if ((_b = draft.palette) === null || _b === void 0 ? void 0 : _b.secondary) {
            draft.palette.secondary = augmentColor({ color: draft.palette.secondary });
        }
    });
    return clientThemeObj;
};
